import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  Settings,
  CreditCard,
  BookOpen,
  HelpCircle,
  LogOut,
  FolderOpen,
  FileText,
  Library,
  Building2,
  Users,
  BarChart3,
  Store,
  GraduationCap,
  UserCheck,
  ClipboardCheck,
  Mic,
  MapPin,
  Sparkles,
  Send,
  TrendingUp,
  Bot,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { getReportsVariant } from '../../lib/permissions';

const Sidebar = ({ isCollapsed, onToggle }) => {
  const { user, logout, isBlogAdmin, isPlatformSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const isProduction = process.env.NODE_ENV === 'production';
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isRightEdgeHovered, setIsRightEdgeHovered] = useState(false);
  // Dropdown groups the user has opened or closed by hand, keyed by item id. A group the user
  // has not touched is open exactly when it contains the current page (see isGroupOpen).
  const [openGroups, setOpenGroups] = useState({});

  // Check if we're on mobile/tablet
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isOrgAdmin = user?.accountType === 'org' && user?.role === 'admin';
  const isDepartmentAdmin = user?.role === 'department_admin' || user?.role === 'dept_admin';
  const isDevEnv = process.env.REACT_APP_PUBLIC_URL?.includes('dev.');

  // `id` is the testid key — kept stable and independent of `label` (display copy)
  // so renaming a nav label in the UI can never silently break an E2E locator.
  const libraryChildren = [
    { id: 'assessment', label: 'Assessment', icon: ClipboardCheck, href: '/dashboard/library/assessments' },
    { id: 'interview', label: 'Interview', icon: Mic, href: '/dashboard/library/ai-interviews' },
    { id: 'candidate', label: 'Candidate', icon: Users, href: '/dashboard/library/candidates' },
  ];

  if (user?.features?.store_list === true) {
    libraryChildren.push({ id: 'store-list', label: 'Store List', icon: Store, href: '/dashboard/store-list' });
  }
  if (user?.features?.city_list === true) {
    libraryChildren.push({ id: 'city-codes', label: 'City Codes', icon: MapPin, href: '/dashboard/library/cities' });
  }

  // Navigation items: Job Posts, Resources, Calendar, Reports, Library, Support, Your Account.
  // Only entries gated by `featureKey` or `visible` are filtered out (see the .filter()
  // below), so Library IS shown — the old "hidden for now" note here was stale.
  // Note: the Job Posts entry points at /dashboard/job-post, which renders
  // ResultsAnalytics. pages/job-posts/JobPosts.js is dead code (see docs/testing/LIVE_SURFACE_MAP.md).
  const navigationItems = [
    {
      id: 'job-posts',
      label: 'Job Posts',
      icon: Briefcase,
      href: '/dashboard/job-post',
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: FolderOpen,
      href: '/dashboard/resources',
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: Calendar,
      href: '/dashboard/calendar',
    },
    {
      // One entry for all three report variants (GoColours / Ajanta MR / Burger King): they
      // share this route and ReportsRouteGuard picks the page, so a single `visible` check
      // keeps the nav in step with the guard and cannot render a duplicate link.
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      href: '/dashboard/reports',
      visible: getReportsVariant(user) !== null,
    },
    {
      id: 'library',
      label: 'Library',
      icon: Library,
      // /dashboard/library/questions has no entry of its own but still belongs to Library.
      matchPrefixes: ['/dashboard/library', '/dashboard/store-list'],
      childTestIdPrefix: 'library-tab',
      children: libraryChildren,
    },
    {
      id: 'support',
      label: 'Support',
      icon: HelpCircle,
      href: '/dashboard/help',
    },
    {
      id: 'your-account',
      label: 'Your Account',
      icon: User,
      href: '/dashboard/profile',
    },
  ];

  // Organization section: org admin and dept admin both see "Teams" (same UI pattern; dept admin sees members view)
  const organizationNavItems = [];
  if (isOrgAdmin || isDepartmentAdmin) {
    organizationNavItems.push({
      id: 'teams',
      label: 'Teams',
      icon: Building2,
      href: '/dashboard/organization',
    });
  }

  // Blog-only managers get a Blogs entry into the in-dashboard CMS. Full
  // super-admins are excluded here — they already manage blogs from /internal.
  const blogNavItems = [];
  if (isBlogAdmin && !isPlatformSuperAdmin) {
    blogNavItems.push({
      id: 'blogs',
      label: 'Blogs',
      icon: FileText,
      href: '/dashboard/blogs',
    });
  }

  // Merge nav: Job Posts, then Resources, Calendar, Library, then Teams, then Blogs, then rest
  const mainNavItems = [
    navigationItems[0],
    ...navigationItems.slice(1, 4),
    ...organizationNavItems,
    ...blogNavItems,
    ...navigationItems.slice(4),
  ].filter(
    (item) =>
      (!item.featureKey || user?.features?.[item.featureKey] === true) &&
      item.visible !== false
  );

  // Agent dropdowns. With the `training_agent` feature on, every hiring page moves into a
  // "Hiring Agent" dropdown and the training pages sit in a "Training Agent" dropdown.
  // Support and Your Account stay at the top level because they belong to both.
  // Without the feature the nav is the flat list above, exactly as before.
  const hasTrainingAgent = user?.features?.training_agent === true;
  const sharedNavIds = ['support', 'your-account'];
  const trainingNavItems = [
    // `exact`: the other training pages live under /dashboard/training/* too. A course's own
    // builder (/dashboard/training/courses/:id) still belongs to Courses.
    {
      id: 'training-courses',
      label: 'Courses',
      icon: BookOpen,
      href: '/dashboard/training',
      exact: true,
      alsoActivePrefixes: ['/dashboard/training/courses'],
    },
    { id: 'training-create', label: 'Create with AI', icon: Sparkles, href: '/dashboard/training/new' },
    { id: 'training-assign', label: 'Assign', icon: Send, href: '/dashboard/training/assign' },
    { id: 'training-analytics', label: 'Analytics', icon: TrendingUp, href: '/dashboard/training/analytics' },
  ];
  const visibleNavItems = hasTrainingAgent
    ? [
        {
          id: 'hiring-agent',
          label: 'Hiring Agent',
          icon: UserCheck,
          agent: true,
          children: mainNavItems.filter((item) => !sharedNavIds.includes(item.id)),
        },
        {
          id: 'training-agent',
          label: 'Training Agent',
          icon: GraduationCap,
          agent: true,
          children: trainingNavItems,
        },
        ...mainNavItems.filter((item) => sharedNavIds.includes(item.id)),
      ]
    : mainNavItems;

  // Karan, the AI recruiting agent: its own dropdown just above Support / Your Account, in
  // either layout. Without the `karan_agent` feature nothing here changes.
  const hasKaranAgent = user?.features?.karan_agent === true;
  const karanAgentGroup = {
    id: 'karan-agent',
    label: 'Karan',
    icon: Bot,
    agent: true,
    children: [
      {
        id: 'karan-roles',
        label: 'Roles',
        icon: Briefcase,
        href: '/dashboard/karan',
        exact: true,
        alsoActivePrefixes: ['/dashboard/karan/roles'],
      },
      { id: 'karan-new', label: 'New role', icon: Sparkles, href: '/dashboard/karan/new' },
    ],
  };
  const navItemsToRender = hasKaranAgent
    ? [
        ...visibleNavItems.filter((item) => !sharedNavIds.includes(item.id)),
        karanAgentGroup,
        ...visibleNavItems.filter((item) => sharedNavIds.includes(item.id)),
      ]
    : visibleNavItems;

  // Check if a nav item is active (Job Posts is landing page, so /dashboard also highlights it)
  const isActive = (item) => {
    if (item.href === '/dashboard/job-post') {
      return location.pathname === '/dashboard' || location.pathname.startsWith(item.href);
    }
    if ((item.alsoActivePrefixes || []).some((prefix) => location.pathname.startsWith(prefix))) return true;
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  };

  // A group is active when the current page is one of its children, at any depth.
  const isGroupActive = (group) =>
    (group.matchPrefixes || []).some((prefix) => location.pathname.startsWith(prefix)) ||
    group.children.some((child) => (child.children ? isGroupActive(child) : isActive(child)));

  const isGroupOpen = (group) => openGroups[group.id] ?? isGroupActive(group);

  const toggleGroup = (group) => {
    // Collapsed, the children have nowhere to show: expand the sidebar with the group open.
    if (isCollapsed) {
      setOpenGroups((prev) => ({ ...prev, [group.id]: true }));
      onToggle();
      return;
    }
    setOpenGroups((prev) => ({ ...prev, [group.id]: !isGroupOpen(group) }));
  };

  const renderNavItem = (item, depth = 0, testIdPrefix) => {
    const testId = testIdPrefix ? `${testIdPrefix}-${item.id}` : `nav-${item.id}-link`;
    const isChild = depth > 0;
    const iconClass = isChild ? 'w-4 h-4' : 'w-5 h-5';

    // Collapsed sidebar: an agent's pages show directly as icons, one block per agent.
    if (item.agent && isCollapsed) {
      return (
        <div
          key={item.id}
          data-testid={testId}
          className="space-y-1 border-t border-gray-200 pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0"
        >
          {item.children.map((child) => renderNavItem(child, 0))}
        </div>
      );
    }

    if (item.children) {
      const open = isGroupOpen(item);
      const active = isGroupActive(item);
      return (
        <div key={item.id}>
          <button
            type="button"
            data-testid={testId}
            aria-expanded={open}
            title={isCollapsed ? item.label : undefined}
            onClick={() => toggleGroup(item)}
            className={cn(
              'flex items-center w-full pr-3 text-sm transition-all duration-200 group relative rounded-r-lg',
              isChild ? 'pl-3 py-2' : 'pl-4 py-2.5',
              isCollapsed ? 'justify-center rounded-lg' : '',
              item.agent
                ? cn('font-semibold', active ? 'text-[#007BFF]' : 'text-gray-800 hover:bg-[#007BFF]/5')
                : cn(
                    'font-medium',
                    active
                      ? 'bg-[#007BFF]/10 text-[#007BFF]'
                      : 'text-gray-600 hover:bg-[#007BFF]/5 hover:text-gray-900'
                  )
            )}
          >
            {active && !item.agent && !isCollapsed && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#007BFF] rounded-r-full" />
            )}
            <item.icon
              className={cn(
                iconClass,
                'transition-colors flex-shrink-0',
                active ? 'text-[#007BFF]' : 'text-gray-600 group-hover:text-[#007BFF]',
                isCollapsed ? '' : 'mr-3'
              )}
            />
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left truncate">{item.label}</span>
                {open ? (
                  <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                )}
              </>
            )}
          </button>
          {!isCollapsed && (
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div className="ml-6 pl-2 border-l border-gray-200 py-1 space-y-0.5">
                    {item.children.map((child) =>
                      renderNavItem(child, depth + 1, item.childTestIdPrefix)
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      );
    }

    const active = isActive(item);
    return (
      <Link
        key={item.href}
        to={item.href}
        data-testid={testId}
        title={isCollapsed ? item.label : undefined}
        className={cn(
          'flex items-center pr-3 text-sm font-medium transition-all duration-200 group relative rounded-r-lg',
          isChild ? 'pl-3 py-2' : 'pl-4 py-2.5',
          isCollapsed ? 'justify-center rounded-lg' : '',
          active
            ? 'bg-[#007BFF]/10 text-[#007BFF]'
            : 'text-gray-600 hover:bg-[#007BFF]/5 hover:text-gray-900'
        )}
      >
        {/* Active indicator */}
        {active && !isCollapsed && (
          <div
            className={cn(
              'absolute left-0 top-1/2 -translate-y-1/2 w-1 bg-[#007BFF] rounded-r-full',
              isChild ? 'h-5' : 'h-6'
            )}
          />
        )}
        <item.icon
          className={cn(
            iconClass,
            'transition-colors flex-shrink-0',
            active ? 'text-[#007BFF]' : 'text-gray-600 group-hover:text-[#007BFF]',
            isCollapsed ? '' : 'mr-3'
          )}
        />
        {!isCollapsed && (
          <span className={cn('flex-1 truncate', active && 'font-semibold')}>{item.label}</span>
        )}
      </Link>
    );
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '[') {
        e.preventDefault();
        onToggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onToggle]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('[data-user-menu]')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    logout(() => {
      navigate('/');
    });
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !isCollapsed && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'h-full bg-[#F7F9FC] border-r border-gray-200/80 transition-all duration-300 ease-in-out flex flex-col relative',
          'shadow-sm',
          // Desktop: full-height column (parent handles stickiness)
          !isMobile && (isCollapsed ? 'w-16' : 'w-56'),
          // Mobile: fixed overlay
          isMobile &&
            cn(
              'fixed left-0 top-0 z-50 shadow-xl h-full',
              isCollapsed ? 'w-16' : 'w-56',
              !isCollapsed ? 'translate-x-0' : '-translate-x-full'
            )
        )}
      >
        {/* Branding Section - Top of Sidebar */}
        {!isCollapsed && (
          <div className="pl-4 pr-4 py-5 border-b border-gray-100 bg-[#F7F9FC]">
            <div className="flex items-center">
              {/* Logo image */}
              <img
                src="/logo.svg"
                alt="TalentCrux Logo"
                className="w-[64px] h-[64px] object-contain flex-shrink-0"
              />
              <span className="ml-1 font-bold text-xl text-gray-900 tracking-tight align-middle">
                TalentCrux
              </span>
            </div>
          </div>
        )}

        {/* Right-edge hover zone: collapse button appears when hovering the far right of sidebar */}
        {!isCollapsed && !isMobile && (
          <div
            onMouseEnter={() => setIsRightEdgeHovered(true)}
            onMouseLeave={() => setIsRightEdgeHovered(false)}
            className="absolute top-0 right-0 bottom-0 w-12 z-10 flex items-center justify-center"
          >
            <button
              onClick={onToggle}
              className={cn(
                'p-1.5 rounded-lg transition-all duration-200',
                'bg-white hover:bg-[#007BFF]/10 border border-gray-200 shadow-sm hover:shadow',
                'text-gray-500 hover:text-[#007BFF] hover:border-[#007BFF]/30',
                isRightEdgeHovered
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-90 pointer-events-none'
              )}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Collapsed branding - just icon */}
        {isCollapsed && (
          <div className="flex flex-col items-center border-b border-gray-100 bg-[#F7F9FC] py-3 gap-2">
            <img
              src="/logo.svg"
              alt="TalentCrux Logo"
              className="w-[42px] h-[42px] object-contain"
            />
            <button
              onClick={onToggle}
              className={cn(
                'p-1.5 rounded-lg transition-all duration-200',
                'bg-gray-100 hover:bg-[#007BFF]/10 active:bg-[#007BFF]/20',
                'text-gray-500 hover:text-[#007BFF]',
                'border border-gray-200 hover:border-[#007BFF]/30'
              )}
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="pl-4 pr-2 space-y-1">
            {navItemsToRender.map((item) => renderNavItem(item))}
          </div>
        </nav>

        {/* Account & user actions */}
        {!isCollapsed && (
          <div className="border-t border-gray-200 pl-4 pr-4 py-4 space-y-4">
            <div className="space-y-3 text-sm text-gray-700">
              {/* User summary */}
              <div className="pt-2">
                <div className="relative" data-user-menu>
                  <button
                    type="button"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-2 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold flex-shrink-0">
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user?.name || 'User'}
                      </p>
                    </div>
                  </button>

                  {/* Dropdown menu */}
                  {isUserMenuOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        type="button"
                        onClick={() => {
                          handleLogout();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 bg-primary text-primary-foreground px-4 py-2 rounded-md"
      >
        Skip to content
      </a>
    </>
  );
};

export default Sidebar;
